import { lazy, Suspense, useEffect, useState } from "react";
import { AppShell, Burger, Group, Image } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { RouteConstants } from "../../constants";
import { BasicButton } from "components";
import { LOGO_IMG } from "constants/app";
import classes from "./index.module.css";
import { loadSuperchat } from "../../utils/loadSuperchat";
import { Helmet } from "react-helmet-async";
import { defaultSEO } from "utils/seo";

const Navigation = lazy(() => import("components/navigation"));
const LanguagePicker = lazy(() => import("components/language"));

const {
  ROUTE_CREDIT_REQUEST,
  ROUTE_CREDIT_REQUEST_BASIC,
  ROUTE_CREDIT_REQUEST_EXISTS_SHORT,
  ROUTE_CREDIT_CARD_FORM,
} = RouteConstants;

const Layout = () => {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let loaded = false;

    const load = () => {
      if (loaded) return;
      loadSuperchat();
      loaded = true;
    };

    const timer = setTimeout(load, 6000);

    const onInteraction = () => {
      load();
      cleanup();
    };

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("click", onInteraction);
      window.removeEventListener("scroll", onInteraction);
    };

    window.addEventListener("click", onInteraction);
    window.addEventListener("scroll", onInteraction);

    return cleanup;
  }, []);

  function isFormPath() {
    return (
      location.pathname.includes(ROUTE_CREDIT_REQUEST) ||
      location.pathname.includes(ROUTE_CREDIT_CARD_FORM)
    );
  }

  const onClickHandler = async () => {
    const { trackCustomEvent } = await import("utils/metaPixel");

    trackCustomEvent("ClickStartRequest");
    navigate(ROUTE_CREDIT_REQUEST_BASIC);
  };

  return (
    <>
      <Helmet>
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={defaultSEO.siteName} />
        <meta property="og:image" content={defaultSEO.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Webkredit",
            url: defaultSEO.url,
            logo: `${defaultSEO.url}/assets/logo-full.svg`,
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FinancialService",
            name: "Webkredit",
            url: defaultSEO.url,
          })}
        </script>
      </Helmet>
      <AppShell
        navbar={{
          width: 300,
          breakpoint: "lg",
          collapsed: { desktop: true, mobile: !opened },
        }}
      >
        <AppShell.Header
          className={`${classes.header} ${scrolled ? classes.scrolled : ""} ${isFormPath() ? classes.request : ""}`}
        >
          <Group className={classes.inner}>
            <Burger
              opened={opened}
              color={"white"}
              hiddenFrom={"lg"}
              size={"sm"}
              onClick={toggle}
              className={classes.burger}
              aria-label="Burger menu button"
            />
            <Group justify={"space-between"} style={{ flex: 1 }}>
              <Image
                src={LOGO_IMG}
                alt={"Webkredit.ch logo"}
                className={classes.logo}
                w={200}
                height={45}
              />
              <Group gap={0} visibleFrom={"lg"}>
                <Suspense fallback={null}>
                  <Navigation />
                </Suspense>
              </Group>
              <Group className={classes.controls}>
                <Suspense fallback={null}>
                  <LanguagePicker />
                </Suspense>
                <BasicButton
                  header={true}
                  disabled={
                    location.pathname.includes(ROUTE_CREDIT_REQUEST) &&
                    !location.pathname.includes(ROUTE_CREDIT_REQUEST_EXISTS_SHORT)
                  }
                  onClick={onClickHandler}
                >
                  {t("action.startRequest")}
                </BasicButton>
              </Group>
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Navbar
          py={"md"}
          px={4}
          hiddenFrom={"lg"}
          className={classes.navbar}
          zIndex={299}
        >
          <Suspense fallback={null}>
            <Navigation onNavigate={toggle} />
          </Suspense>
        </AppShell.Navbar>
        <main>
          <Outlet />
        </main>
      </AppShell>
    </>
  );
};

export default Layout;
